import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarCheck, ChartLine, Coffee, House, ListChecks, Trophy, CheckCircle, Users,
} from "@phosphor-icons/react";

/* ----------------------------- VARIANT A -------------------------------- */
/* Tilted 2x3 mosaic of real-app preview cards */
export function HeroVariantA() {
  const tiles = [
    { id: 1, rotate: -8, color: "#0070F3", element: <EventCard /> },
    { id: 2, rotate: 6, color: "#C6392E", element: <StockCard /> },
    { id: 3, rotate: -3, color: "#1E5463", element: <ViewingCard /> },
    { id: 4, rotate: 9, color: "#0A0A0A", element: <StampPreview /> },
    { id: 5, rotate: -6, color: "#0F3024", element: <ActionCard /> },
    { id: 6, rotate: 4, color: "#1A2544", element: <ScoreCard /> },
  ];
  return (
    <div className="relative h-[500px] w-full">
      <div className="absolute inset-0 grid grid-cols-3 gap-5 p-4">
        {tiles.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 24, rotate: t.rotate - 4 }}
            animate={{ opacity: 1, y: 0, rotate: t.rotate }}
            transition={{ delay: 0.15 * i, duration: 0.7, ease: "easeOut" }}
            whileHover={{ rotate: 0, scale: 1.05, zIndex: 10 }}
            className="bg-white border shadow-lg overflow-hidden"
            style={{ borderColor: "#E5E7EB", transformOrigin: "center" }}
          >
            {t.element}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- VARIANT B -------------------------------- */
/* Isometric device stack: phone + tablet + laptop */
export function HeroVariantB() {
  return (
    <div className="relative h-[500px] w-full flex items-center justify-center overflow-hidden">
      {/* Laptop */}
      <motion.div
        initial={{ opacity: 0, x: 40, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.1, duration: 0.7 }}
        className="absolute right-0 bottom-8 w-[380px] shadow-2xl"
        style={{ transform: "perspective(1200px) rotateY(-18deg) rotateX(5deg)" }}
      >
        <div className="bg-zinc-900 rounded-t-lg p-1.5 flex gap-1.5 items-center">
          <div className="w-2 h-2 rounded-full bg-red-400" />
          <div className="w-2 h-2 rounded-full bg-yellow-400" />
          <div className="w-2 h-2 rounded-full bg-green-400" />
        </div>
        <div className="bg-white border-x border-zinc-900" style={{ borderWidth: "0 6px" }}>
          <LaptopScreen />
        </div>
        <div className="bg-zinc-800 rounded-b-md h-2" />
        <div className="bg-zinc-700 h-1" />
      </motion.div>

      {/* Tablet */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.7 }}
        className="absolute left-4 bottom-4 w-[220px] shadow-2xl z-10"
        style={{ transform: "perspective(1200px) rotateY(12deg) rotateX(3deg)" }}
      >
        <div className="bg-zinc-900 p-2 rounded-2xl">
          <div className="bg-white rounded-lg overflow-hidden aspect-[3/4]">
            <TabletScreen />
          </div>
        </div>
      </motion.div>

      {/* Phone (foreground) */}
      <motion.div
        initial={{ opacity: 0, y: 60, rotate: -5 }}
        animate={{ opacity: 1, y: 0, rotate: -4 }}
        transition={{ delay: 0.5, duration: 0.7 }}
        className="absolute left-[28%] bottom-0 w-[160px] shadow-2xl z-20"
      >
        <div className="bg-zinc-900 p-1.5 rounded-[2rem]">
          <div className="bg-white rounded-[1.7rem] overflow-hidden aspect-[9/18]">
            <PhoneScreen />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ----------------------------- VARIANT C -------------------------------- */
/* Single convincing "live" app widget with ticking counters */
export function HeroVariantC() {
  const [count, setCount] = React.useState(23);
  const [chartHeights] = React.useState([40, 70, 55, 85, 60, 95, 75]);

  React.useEffect(() => {
    const id = setInterval(() => setCount((c) => (c >= 29 ? 23 : c + 1)), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative h-[500px] w-full flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7 }}
        className="bg-white shadow-2xl border border-zinc-200 w-[420px]"
      >
        <div className="p-6 border-b border-zinc-200 bg-gradient-to-br from-[#0070F3] to-[#00C4CC] text-white">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] opacity-80">Holborough Lakes Social</p>
          <h3 className="text-2xl font-black mt-1 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Summer BBQ</h3>
          <p className="text-sm mt-1 opacity-90">Saturday 14 Aug · Lake pavilion</p>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Going</div>
              <motion.div
                key={count}
                initial={{ scale: 1.2, color: "#0070F3" }}
                animate={{ scale: 1, color: "#0A0A0A" }}
                transition={{ duration: 0.4 }}
                className="text-4xl font-black tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {count}/30
              </motion.div>
            </div>
            <div className="flex -space-x-2">
              {["#0070F3", "#00C4CC", "#C6392E", "#E94E3B"].map((c, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: c }}>
                  {String.fromCharCode(65 + i)}
                </div>
              ))}
            </div>
          </div>
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
            <motion.div animate={{ width: `${(count / 30) * 100}%` }} transition={{ duration: 0.6 }} className="h-full" style={{ background: "linear-gradient(90deg, #0070F3, #00C4CC)" }} />
          </div>
          <div className="pt-4 border-t border-zinc-100">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">This week · RSVPs</div>
              <div className="text-xs font-semibold text-emerald-600">↑ 18%</div>
            </div>
            <div className="flex items-end gap-1.5 h-16">
              {chartHeights.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 0.2 + i * 0.05, duration: 0.6 }}
                  className="flex-1"
                  style={{ background: i === chartHeights.length - 1 ? "#0070F3" : "#DBEAFE" }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-3 bg-zinc-50 border-t border-zinc-200 flex items-center gap-2 text-[10px] font-mono text-zinc-500">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE · real backend
        </div>
      </motion.div>
    </div>
  );
}

/* ----------------------------- VARIANT D -------------------------------- */
/* Floating card collage — overlapping UI snippets with drift */
export function HeroVariantD() {
  const floats = [
    { id: "rsvp", x: "0%", y: "10%", w: 200, rotate: -4, delay: 0, drift: 6, content: <FloatRSVP /> },
    { id: "stock", x: "38%", y: "0%", w: 180, rotate: 5, delay: 0.15, drift: -5, content: <FloatStock /> },
    { id: "view", x: "62%", y: "30%", w: 210, rotate: -2, delay: 0.3, drift: 4, content: <FloatViewing /> },
    { id: "stamp", x: "8%", y: "55%", w: 170, rotate: 6, delay: 0.45, drift: -4, content: <FloatStamps /> },
    { id: "action", x: "42%", y: "62%", w: 200, rotate: -3, delay: 0.6, drift: 5, content: <FloatAction /> },
  ];
  return (
    <div className="relative h-[500px] w-full">
      {floats.map((f) => (
        <motion.div
          key={f.id}
          initial={{ opacity: 0, y: 30, rotate: f.rotate - 4 }}
          animate={{
            opacity: 1,
            y: [0, f.drift, 0],
            rotate: f.rotate,
          }}
          transition={{
            opacity: { delay: f.delay, duration: 0.7 },
            rotate: { delay: f.delay, duration: 0.7 },
            y: { delay: f.delay, duration: 4 + Math.random() * 2, repeat: Infinity, ease: "easeInOut" },
          }}
          className="absolute bg-white shadow-xl border border-zinc-200"
          style={{ left: f.x, top: f.y, width: f.w }}
        >
          {f.content}
        </motion.div>
      ))}
    </div>
  );
}

/* ----------------------------- MINI UI BITS -------------------------------- */

function EventCard() {
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <CalendarCheck size={14} weight="fill" style={{ color: "#0070F3" }} />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Social</span>
      </div>
      <div className="text-sm font-bold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Summer BBQ</div>
      <div className="text-[10px] text-zinc-500 mt-1">Sat 14 Aug · 7pm</div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs font-black">23/30</div>
        <div className="h-1 flex-1 ml-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: "77%", background: "linear-gradient(90deg, #0070F3, #00C4CC)" }} />
        </div>
      </div>
    </div>
  );
}
function StockCard() {
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <Coffee size={14} weight="fill" style={{ color: "#C6392E" }} />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">On the board</span>
      </div>
      <div className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Cinnamon Bun</div>
      <div className="text-[10px] text-zinc-500">£3.50 · Pastry</div>
      <div className="mt-2 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: "70%", background: "#C6392E" }} />
      </div>
      <div className="flex justify-between mt-2 text-[10px] font-mono">
        <span className="font-bold">8 left</span>
        <span className="text-zinc-500">4 sold</span>
      </div>
    </div>
  );
}
function ViewingCard() {
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <House size={14} weight="fill" style={{ color: "#1E5463" }} />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Viewing</span>
      </div>
      <div className="text-sm font-bold" style={{ fontFamily: "'Outfit', serif" }}>12 Lakeside Walk</div>
      <div className="mt-2 flex items-center gap-1.5">
        <CheckCircle size={12} weight="fill" className="text-emerald-600" />
        <span className="text-xs"><b>Offer</b> · £470,000</span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1 italic">"Loved the garden."</div>
    </div>
  );
}
function StampPreview() {
  return (
    <div className="p-4 text-white" style={{ background: "#0A0A0A" }}>
      <div className="flex items-center gap-2 mb-2">
        <Coffee size={14} weight="fill" style={{ color: "#E94E3B" }} />
        <span className="text-[9px] font-mono uppercase tracking-[0.3em]" style={{ color: "#E94E3B" }}>Coffee Club</span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="aspect-square rounded-full border" style={{ borderColor: i < 7 ? "#E94E3B" : "#333", background: i < 7 ? "#E94E3B" : "transparent" }} />
        ))}
      </div>
      <div className="text-[10px] text-zinc-400 font-mono mt-2">7 / 10</div>
    </div>
  );
}
function ActionCard() {
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks size={14} weight="fill" style={{ color: "#0F3024" }} />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Action</span>
      </div>
      <div className="text-sm font-bold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Share the new playground plans</div>
      <div className="flex items-center gap-2 mt-2">
        <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ background: "#0F3024" }}>R</div>
        <span className="text-[10px] text-zinc-600">Rita · due Fri</span>
      </div>
    </div>
  );
}
function ScoreCard() {
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={14} weight="fill" style={{ color: "#1A2544" }} />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Eclectic</span>
      </div>
      <div className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>M. Brennan · Round 4</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-2xl font-black" style={{ color: "#1A2544" }}>68</div>
        <div className="text-[10px] text-zinc-500 font-mono">NET · ↓ 4</div>
      </div>
    </div>
  );
}

function LaptopScreen() {
  return (
    <div className="p-3 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono text-zinc-500">HLRA · Action Portal</div>
        <div className="flex gap-1 text-[8px] text-zinc-400">open · closed</div>
      </div>
      <div className="space-y-1.5">
        {["Playground plans", "Summer BBQ", "Recycling audit", "AGM minutes"].map((t, i) => (
          <div key={i} className="flex items-center gap-2 p-1.5 border border-zinc-100 rounded">
            <div className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[7px] font-bold" style={{ background: ["#0070F3", "#00C4CC", "#C6392E", "#0F3024"][i] }}>{t[0]}</div>
            <div className="flex-1 text-[10px] truncate">{t}</div>
            <div className="text-[8px] font-mono text-zinc-400">{["OPEN", "OPEN", "DONE", "OPEN"][i]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function TabletScreen() {
  return (
    <div className="p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Holborough</div>
      <h3 className="text-base font-black mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Summer BBQ</h3>
      <div className="mt-3 p-2 bg-gradient-to-br from-[#0070F3] to-[#00C4CC] text-white text-center">
        <div className="text-xl font-black">23/30</div>
        <div className="text-[9px] font-mono opacity-80">GOING</div>
      </div>
      <div className="mt-2 space-y-1">
        {["Anna P.", "Ben K.", "Chris J."].map((n, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[10px]">
            <div className="w-4 h-4 rounded-full bg-zinc-300" />
            <span className="truncate">{n}</span>
            <CheckCircle size={10} weight="fill" className="text-emerald-500 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}
function PhoneScreen() {
  return (
    <div className="p-3 h-full flex flex-col" style={{ background: "#0A0A0A", color: "white" }}>
      <div className="text-[8px] font-mono uppercase tracking-[0.3em]" style={{ color: "#E94E3B" }}>Coffee Club</div>
      <div className="grid grid-cols-5 gap-1 my-4">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="aspect-square rounded-full border" style={{ borderColor: i < 7 ? "#E94E3B" : "#333", background: i < 7 ? "#E94E3B" : "transparent" }} />
        ))}
      </div>
      <div className="text-center mt-auto">
        <div className="text-xl font-black">7 / 10</div>
        <div className="text-[8px] font-mono" style={{ color: "#999" }}>3 MORE</div>
      </div>
    </div>
  );
}

function FloatRSVP() {
  return (
    <div className="p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Users size={12} weight="fill" style={{ color: "#0070F3" }} />
        <span className="text-[8px] font-mono uppercase tracking-widest text-zinc-500">RSVP live</span>
      </div>
      <div className="text-sm font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>23 going</div>
      <div className="text-[10px] text-zinc-500">of 30 seats</div>
      <div className="mt-2 h-1 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full" style={{ width: "77%", background: "linear-gradient(90deg, #0070F3, #00C4CC)" }} />
      </div>
    </div>
  );
}
function FloatStock() {
  return (
    <div className="p-3" style={{ background: "#F5F1E8" }}>
      <div className="text-[8px] font-mono uppercase tracking-widest" style={{ color: "#0F1A14" }}>On the board</div>
      <div className="text-lg font-black mt-1" style={{ color: "#0F1A14", fontFamily: "'Outfit', sans-serif" }}>8 left</div>
      <div className="text-[10px]" style={{ color: "#1F3A2A" }}>Cinnamon buns · £3.50</div>
    </div>
  );
}
function FloatViewing() {
  return (
    <div className="p-3" style={{ background: "#F1EFEA" }}>
      <div className="text-[8px] font-mono uppercase tracking-widest" style={{ color: "#1E5463" }}>Viewing · yes</div>
      <div className="text-sm font-black mt-0.5" style={{ fontFamily: "'Outfit', serif", color: "#0A0A0A" }}>£470,000 offer</div>
      <div className="text-[10px] text-zinc-600 mt-1 italic">"Loved the garden."</div>
    </div>
  );
}
function FloatStamps() {
  return (
    <div className="p-3" style={{ background: "#0A0A0A", color: "white" }}>
      <div className="text-[8px] font-mono uppercase tracking-[0.3em]" style={{ color: "#E94E3B" }}>Coffee Club</div>
      <div className="grid grid-cols-5 gap-0.5 mt-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="aspect-square rounded-full" style={{ background: i < 7 ? "#E94E3B" : "#2A2A2A" }} />
        ))}
      </div>
    </div>
  );
}
function FloatAction() {
  return (
    <div className="p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <CheckCircle size={12} weight="fill" className="text-emerald-500" />
        <span className="text-[8px] font-mono uppercase tracking-widest text-zinc-500">Closed · today</span>
      </div>
      <div className="text-sm font-bold leading-tight">Playground plans shared with residents</div>
      <div className="text-[10px] text-zinc-500 mt-1">Rita · 2h ago</div>
    </div>
  );
}

/* ----------------------------- PREVIEW PAGE -------------------------------- */
export default function HeroPreview() {
  const variants = [
    { key: "a", label: "A · App Mosaic", desc: "Tilted grid of real-app preview tiles, each its own palette.", Component: HeroVariantA },
    { key: "b", label: "B · Device Stack", desc: "Isometric phone + tablet + laptop, each rendering a real app.", Component: HeroVariantB },
    { key: "c", label: "C · Live Mini-Dashboard", desc: "A single convincing live app widget with ticking counter + chart.", Component: HeroVariantC },
    { key: "d", label: "D · Floating Card Collage", desc: "Overlapping UI snippets with gentle drift — portfolio mood.", Component: HeroVariantD },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="hero-preview-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/" className="text-xs font-mono opacity-60 hover:opacity-100">← Back to home</Link>
        <h1 className="text-4xl font-black tracking-tight mt-4 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Hero variants · pick one</h1>
        <p className="text-zinc-600 mb-10">Each variant replaces the right-hand column of the Home hero. Copy, CTAs and layout stay the same.</p>

        <div className="space-y-10">
          {variants.map((v) => {
            const C = v.Component;
            return (
              <section key={v.key} className="bg-white border border-zinc-200 shadow-sm overflow-hidden" data-testid={`hero-variant-${v.key}`}>
                <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.label}</h2>
                    <p className="text-xs text-zinc-500">{v.desc}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-6 p-6 min-h-[520px]">
                  <div className="lg:col-span-6">
                    <p className="overline mb-3 text-[hsl(var(--appistry-signal))]">Practical Apps, Thoughtfully Built</p>
                    <h3 className="text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Small, sharp apps that pay for themselves.</h3>
                    <p className="text-zinc-600">Appistry builds the unglamorous apps that quietly take work off your plate — intake, inbox, scorecards, bookings. We grow with you to keep them sharp.</p>
                  </div>
                  <div className="lg:col-span-6">
                    <C />
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
